import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SEOHead } from '../components/SEOHead';
import { useAuth } from '@/core/AuthContext';

/**
 * Landing page for logged-out users
 * 
 * Key messaging:
 * - Personal trainer available 24/7
 * - Accountability through phone call reminders and check-ins
 * - Be your gym partner - never train alone
 * - Financial motivation - premium pricing means you show up
 */
export function Landing() {
  const navigate = useNavigate();
  const { user, userData, loading } = useAuth();

  // Redirect signed-in users to their dashboard
  useEffect(() => {
    if (loading) return;
    
    if (user && userData) {
      if (userData.role === 'trainer') {
        navigate('/trainer', { replace: true });
      } else {
        navigate('/client', { replace: true });
      }
    }
  }, [user, userData, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Don't render landing page content if user is signed in (will redirect)
  if (user && userData) {
    return null;
  }


  const features = [
    {
      icon: '🎯',
      title: 'Personalized Training',
      description: 'Get custom workouts tailored to your goals, equipment, and experience level. Available 24/7, whenever you need guidance.',
    },
    {
      icon: '📞',
      title: 'Real Accountability',
      description: 'Your coach calls you before each workout to confirm attendance. Personal phone reminders that actually get you moving.',
    },
    {
      icon: '💪',
      title: 'Never Train Alone',
      description: 'Your coach is always with you - providing motivation, form cues, and encouragement throughout every workout.',
    },
    {
      icon: '💎',
      title: 'Premium Commitment',
      description: 'High-value subscription means you show up. Use it or lose it - financial motivation that actually works.',
    },
  ];

  const benefits = [
    'Personalized workout programs',
    'Real-time form guidance',
    'Phone call workout reminders',
    'Progress tracking & analytics',
    'Adaptive difficulty scaling',
    'Equipment-based customization',
  ];

  return (
    <>
      <SEOHead
        title="Start Grounded | AI-Powered Personal Training"
        description="Start your fitness journey grounded in accountability. AI trainers, phone call reminders, and real commitment tools."
        canonicalUrl="https://startgrounded.com"
        ogImage="https://startgrounded.com/og-image.png"
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 safe-top">
          <div className="flex justify-between items-center">
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-2xl font-bold bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent"
            >
              Start Grounded
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Button variant="primary" onClick={() => navigate('/phone-login')}>
                Get Started
              </Button>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6"
            >
              Your{' '}
              <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent">
                Gym Partner
              </span>
              <br />
              That Never Quits
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl sm:text-2xl text-gray-600 dark:text-slate-400 mb-8 max-w-3xl mx-auto"
            >
              Stop going to the gym alone. Get personalized workouts, real accountability,
              and a trainer who's available 24/7 to keep you on track.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                size="lg"
                variant="primary"
                onClick={() => navigate('/phone-login')}
                className="text-lg px-8 py-4 shadow-2xl shadow-primary-500/30 hover:shadow-primary-500/50 transition-all"
              >
                <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Learn More
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-primary-500/10 to-accent-500/10 dark:from-primary-500/5 dark:to-accent-500/5 rounded-full blur-3xl" />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h3 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need to Stay Committed
            </h3>
            <p className="text-xl text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              No more excuses. No more skipping workouts. Your trainer keeps you accountable
              and motivated every single day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card variant="elevated" padding="lg" className="h-full hover:shadow-2xl transition-shadow">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 dark:text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Start Grounded Works
              </h3>
              <p className="text-lg text-gray-600 dark:text-slate-400 mb-8">
                We understand the struggle. You buy a gym membership, go for a week, then life gets
                busy and you stop showing up. Sound familiar?
              </p>
              <p className="text-lg text-gray-600 dark:text-slate-400 mb-8">
                Start Grounded is different. With premium pricing and real accountability,
                you'll actually show up. Because when you invest in yourself, you don't want to
                waste it.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-success-500 to-success-600 flex items-center justify-center text-white text-sm">
                      ✓
                    </div>
                    <span className="text-gray-700 dark:text-slate-300 text-lg">
                      {benefit}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card variant="elevated" padding="lg" className="bg-gradient-to-br from-primary-500 to-primary-600 text-white">
                <div className="text-6xl mb-6 text-center">💰</div>
                <h4 className="text-3xl font-bold mb-4 text-center">
                  Premium Membership
                </h4>
                <p className="text-white/90 text-center mb-6 text-lg">
                  High-value subscription = high commitment
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/80">Personal Trainer</span>
                    <span className="font-semibold">✓</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/80">Unlimited Workouts</span>
                    <span className="font-semibold">✓</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/80">Phone Call Accountability</span>
                    <span className="font-semibold">✓</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/80">Progress Analytics</span>
                    <span className="font-semibold">✓</span>
                  </div>
                </div>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => navigate('/phone-login')}
                  className="w-full text-primary-600 hover:text-primary-700"
                >
                  Start Your Journey
                </Button>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Stop Making Excuses?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join Start Grounded today and get a personal trainer who won't let you quit.
              Your future self will thank you.
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate('/phone-login')}
              className="text-lg px-10 py-5 shadow-2xl hover:shadow-white/30 transition-all"
            >
              Start Free Trial Now
              <svg className="w-6 h-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h4 className="text-xl font-bold bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent mb-4">
              Start Grounded
            </h4>
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              Train smarter, move better, never alone.
            </p>
            <div className="flex justify-center gap-6 mb-4">
              <a href="/privacy" className="text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400">
                Terms of Service
              </a>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-500">
              © 2026 Start Grounded. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
